import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PAGE_NAMES, ROLES, ROLES_TYPE } from '../../../core/helpers/constants';
import { UtilityService } from '../../../core/helpers/utility.service';
import { IPermission, IUser } from '../../../core/models/auth.models';
import { ITask } from '../../../core/models/tasks.models';
import { ISearch } from '../../../core/models/search.model';
import { IColumn, LoadDataFromServer } from '../../../shared/components/datatable/datatable.model';
import { AppService } from '../../../store/app.service';
import { TasksApiService } from '../tasks.apiservice';
import { TasksService } from '../tasks.service';
import { FormBuilder, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-tasks-list',
  templateUrl: './tasks-list.component.html',
  styleUrls: ['./tasks-list.component.scss']
})

export class TasksListComponent implements OnInit {
  doWhat: IPermission = {} as IPermission;
  activeUser: IUser = {} as IUser;
  ROLES = ROLES;
  showCompleted: boolean = false;
  showDashboard: boolean = true; // Add property to toggle between dashboard and datatable
  selectionResetToken = 0;

  columns: Array<IColumn> = [];
  completedTasksColumns: Array<IColumn> = []
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  allTasksData: Array<ITask> = new Array<ITask>();
  todoTasksData: Array<ITask> = new Array<ITask>();
  completedTasksData: Array<ITask> = new Array<ITask>();
  public userRoles: Array<number> = [];
  showOnlyMyTasks: boolean = true;
  showUserFilter: boolean = true;
  selectedUserId: string | null = null;
  showTasksDue: string = 'All';
  dtForm: FormGroup;
  savedSearchId: any = null;
  taskSavedSearches: ISearch[] = [];
  selectedSavedSearchId: string | null = null;
  private loadEvent!: LoadDataFromServer;
  dataTotalCount: number = 0;
  constructor(
    public uService: UtilityService,
    private appService: AppService,
    private tasksService: TasksService,
    private route: ActivatedRoute,
    private taskApiService: TasksApiService,
    private formBuilder: FormBuilder) {
    this.userRoles = [...this.uService.getUserRoles()];
    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.activeUser = u;
      })

    this.columns.push({ title: 'TASK_TITLE', field: 'title', sortable: true, searchable: true, translate: true } as IColumn);
    this.columns.push({ title: 'CLIENT', field: 'clientName', sortable: true, searchable: true, anchorLink: true, anchorType: 'client', anchorParamsField: 'clientId', translate: true } as IColumn);
    this.columns.push({ title: 'CAMPAIGN', field: 'campaignName', sortable: true, searchable: true, anchorLink: true, anchorType: 'campaign', anchorParamsField: 'campaignId', translate: true } as IColumn);
    this.columns.push({ title: 'DUE_DATE', field: 'dueDate', sortable: true, pipe: 'P_AppDateFormat', pipeArgs: ['MM/DD/YYYY'], translate: true } as IColumn);
    this.columns.push({ title: 'ASSIGNED_USER', field: 'assignedUserName', sortable: true, searchable: true, translate: true } as IColumn);


    this.completedTasksColumns.push({ title: 'TASK_TITLE', field: 'title', sortable: true, searchable: true, translate: true } as IColumn);
    this.completedTasksColumns.push({ title: 'CLIENT', field: 'clientName', sortable: true, searchable: true, anchorLink: true, anchorType: 'client', anchorParamsField: 'clientId', translate: true } as IColumn);
    this.completedTasksColumns.push({ title: 'CAMPAIGN', field: 'campaignName', sortable: true, searchable: true, anchorLink: true, anchorType: 'campaign', anchorParamsField: 'campaignId', translate: true } as IColumn);
    this.completedTasksColumns.push({ title: 'DATE_COMPLETED', field: 'completedOn', sortable: true, pipe: 'P_AppDateFormat', pipeArgs: ['YYYY/MM/DD', true], translate: true } as IColumn);
    this.completedTasksColumns.push({ title: 'ASSIGNED_USER', field: 'assignedUserName', sortable: true, searchable: true, translate: true } as IColumn);

    this.dtForm = this.formBuilder.group({
      showTasksDue: [this.showTasksDue]
    });

    // Restore showDashboard state from localStorage if available
    const storedShowDashboard = localStorage.getItem('tasksShowDashboard');
    if (storedShowDashboard !== null) {
      this.showDashboard = storedShowDashboard === 'true';
    }
  }

  ngOnInit(): void {
    this.appService.setPageTitle$('TASKS');
    this.savedSearchId = this.route.snapshot.queryParams['savedsearchid'];
    this.loadSavedSearches();
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Tasks);
    this.appService.getAllTasks$()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: any) => {
        this.todoTasksData = response.data;
        this.dataTotalCount = response.totalCount;
      });

    this.appService.getForceLoadingTasks()
      .pipe(takeUntil(this.destroyed$)).subscribe((res: Boolean) => {
        if (res) {
          this.loadData();
          this.appService.setForceLoadingTasks(false);
        }
      })
    this.configureTasksService();
    this.loadData();
  }

  private loadSavedSearches() {
    this.appService.fetchSearches();
    this.appService.getAllSearches$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((searches: ISearch[]) => {
        this.taskSavedSearches = (searches || [])
          .filter(search => search.resultType?.toLowerCase() === 'task');
        this.selectedSavedSearchId = this.taskSavedSearches.some(search => search.id === this.savedSearchId)
          ? this.savedSearchId
          : null;
      });
  }

  configureTasksService() {
    this.tasksService.onSave.pipe(takeUntil(this.destroyed$))
      .subscribe((v: any) => {
        if (v) {
          this.loadData();
        }
      });
    this.tasksService.onDelete.pipe(takeUntil(this.destroyed$))
      .subscribe((v: any) => {
        if (v) {
          this.loadData();
        }
      });
    this.tasksService.onMarkAsComplete.pipe(takeUntil(this.destroyed$))
      .subscribe((v: any) => {
        if (v) {
          this.loadData();
        }
      });
    this.tasksService.onBulkEditComplete.pipe(takeUntil(this.destroyed$))
      .subscribe((v: any) => {
        if (v) {
          this.selectionResetToken++;
          this.loadData();
        }
      });
  }

  onLoadingData(event: LoadDataFromServer) {
    this.loadEvent = event;
    this.loadData();
  }

  onUserFilterChange(userId: string | null) {
    this.selectedUserId = userId ? String(userId).trim() : null;
    if (this.selectedUserId) {
      this.showOnlyMyTasks = false;
    }
    this.loadData();
  }

  onSavedSearchChange(savedSearchId: string | null) {
    this.selectedSavedSearchId = savedSearchId || null;
    this.savedSearchId = savedSearchId || '';
    this.loadData();
  }

  editRow(evt: any) {
    evt.allTasksloadingParameters = this.getLoadingParameters(this.showCompleted);
    this.tasksService.openEditTaskModal(evt);
  }

  bulkEditRows(selectedIds: Array<string>) {
    const selectedTasks = this.todoTasksData.filter(task => selectedIds.includes(task.id));
    if (selectedTasks.length === 0) {
      return;
    }

    this.tasksService.openBulkEditTaskModal(selectedTasks);
  }

  deleteRows(evt: any) {
    const ids = Array.isArray(evt) ? evt : [evt?.id ?? evt];
    const validIds = ids.filter((id: any) => id !== undefined && id !== null);

    if (validIds.length === 0) {
      return;
    }

    forkJoin(validIds.map((id: string) => this.taskApiService.deleteTask(id)))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.loadData();
      })
  }

  onShowMyTasksChange() {
    if (this.showOnlyMyTasks) {
      this.selectedUserId = null;
    }
    this.loadData();
  }

  onDueDateFilterChange() {
    this.showTasksDue = this.dtForm.get('showTasksDue')?.value;
    this.loadData();
  }

  getLoadingParameters(showCompleted: boolean) {
    var yourDate = new Date();
    const offset = yourDate.getTimezoneOffset();
    yourDate = new Date(yourDate.getTime() - (offset*60*1000))
    let todayAtUserComputer = yourDate.toISOString().split('T')[0];
    const baseRequest = showCompleted ? {
      savedSearchId: this.savedSearchId,
      showOnlyMyTasks: this.showOnlyMyTasks && !this.selectedUserId,
      dueDate: this.showTasksDue,
      completion: 'Completed',
      todayAtUserComputer,
    } : {
      savedSearchId: this.savedSearchId,
      showOnlyMyTasks: this.showOnlyMyTasks && !this.selectedUserId,
      dueDate: this.showTasksDue,
      completion: 'ToDo',
      todayAtUserComputer,
    };

    const serverPagingRequest = this.showUserFilter ? {} : (this.loadEvent ?? {});

    return {
      ...baseRequest,
      ...serverPagingRequest
    };
  }

  loadData() {
    this.appService.fetchAllTasks(this.getLoadingParameters(this.showCompleted));
  }

  showCompletedTasks() {
    this.showCompleted = !this.showCompleted;

    this.appService.fetchAllTasks(this.getLoadingParameters(this.showCompleted));
  }

  toggleTab(showCompleted: boolean) {
    this.showCompleted = showCompleted;
    this.loadData();
  }

  // Add method to toggle between dashboard and datatable
  toggleView() {
    this.showDashboard = !this.showDashboard;
    // Persist showDashboard state to localStorage
    localStorage.setItem('tasksShowDashboard', this.showDashboard.toString());
  }
}
